import { type ReactNode, useEffect, useRef, useState } from 'react';

// Owner-facing visibility control for an interactive page (the Claude-artifacts
// access model: private / domain / public). Shared by the in-chat PageCard
// (primitives.tsx) and the standalone artifact wrapper's control-bar island, so
// the control lives in one place — like pageRefresh.tsx for the refresh footer.
//
// The eve GET returns the current value AND whether this viewer may change it
// (`canManage` is false for guests, whose pages are private-only). On any
// failure — not the owner, no store, an old eve without the route, or the dev
// gallery with no backend — the hook leaves `visibility` null and the control
// renders nothing (degrade, never throw). A non-null `visibility` means the
// route 200'd, i.e. this viewer owns the page (the route is owner-scoped).

// Viewer-facing options: `trigger` is the short control label, `label` the
// one-line menu description.
export const VISIBILITY_OPTIONS = [
  { value: 'private', trigger: 'Only me', label: 'Only you can open this page' },
  { value: 'domain', trigger: 'Nova Labs', label: 'Anyone signed in at Nova Labs' },
  { value: 'public', trigger: 'Public', label: 'Anyone with the link' },
] as const;

/** Small glyph per visibility state (lock / building / globe), inheriting color. */
export function visIcon(kind: string): ReactNode {
  const common = { width: 14, height: 14, viewBox: '0 0 24 24', fill: 'none', 'aria-hidden': true } as const;
  if (kind === 'private') {
    return (
      <svg {...common}>
        <rect x="4.5" y="10.5" width="15" height="9.5" rx="2" stroke="currentColor" strokeWidth="1.8" />
        <path d="M8 10.5V7.5a4 4 0 0 1 8 0v3" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    );
  }
  if (kind === 'domain') {
    return (
      <svg {...common}>
        <rect x="5" y="6.5" width="14" height="13.5" rx="1.5" stroke="currentColor" strokeWidth="1.8" />
        <path d="M3 20.5h18" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        <path d="M9 10.5h.01M15 10.5h.01M9 14.5h.01M15 14.5h.01" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    );
  }
  return (
    <svg {...common}>
      <circle cx="12" cy="12" r="8.5" stroke="currentColor" strokeWidth="1.8" />
      <path d="M3.5 12h17M12 3.5c2.6 2.3 2.6 14.7 0 17M12 3.5c-2.6 2.3-2.6 14.7 0 17" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

export interface PageVisibility {
  /** Current visibility, or null when unknown/not-owner (control hidden). A
   *  non-null value means the owner-scoped GET 200'd → this viewer is the owner. */
  visibility: string | null;
  /** Whether this viewer may change it (false for guests — private-only). */
  canManage: boolean;
  saving: boolean;
  saveError: boolean;
  changeVisibility: (next: string) => void;
}

/** Load + mutate a page's visibility. Self-contained fetch with its own error
 *  handling, so a failure here never affects the caller's other state. */
export function usePageVisibility(pageId: string | null): PageVisibility {
  const [visibility, setVisibility] = useState<string | null>(null);
  const [canManage, setCanManage] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(false);

  useEffect(() => {
    if (!pageId) return;
    let alive = true;
    void (async () => {
      try {
        const res = await fetch(`/api/eve/v1/artifacts/${pageId}/visibility`, { credentials: 'same-origin' });
        if (!res.ok) return;
        const data = (await res.json()) as { visibility?: string; canManage?: boolean };
        if (alive && data.visibility) {
          setVisibility(data.visibility);
          setCanManage(Boolean(data.canManage));
        }
      } catch {
        /* leave the control hidden */
      }
    })();
    return () => {
      alive = false;
    };
  }, [pageId]);

  const changeVisibility = (next: string) => {
    if (!pageId || next === visibility) return;
    const prev = visibility;
    setVisibility(next); // optimistic
    setSaving(true);
    setSaveError(false);
    void (async () => {
      try {
        const res = await fetch(`/api/eve/v1/artifacts/${pageId}/visibility`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ visibility: next }),
        });
        if (!res.ok) throw new Error(`visibility ${res.status}`);
      } catch {
        setVisibility(prev); // revert on failure
        setSaveError(true);
      } finally {
        setSaving(false);
      }
    })();
  };

  return { visibility, canManage, saving, saveError, changeVisibility };
}

/** The owner's visibility dropdown, or a read-only chip for a guest owner
 *  (canManage false). Renders nothing when there's no visibility (not owner /
 *  not loaded). `dropUpNearViewportBottom` opens the menu upward when the
 *  trigger sits low in the viewport (chat cards near the scroller bottom). */
export function VisibilityControl({ state, dropUpNearViewportBottom = false }: { state: PageVisibility; dropUpNearViewportBottom?: boolean }) {
  const { visibility, canManage, saving, saveError, changeVisibility } = state;
  const [menuOpen, setMenuOpen] = useState(false);
  const [dropUp, setDropUp] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const current = visibility ? (VISIBILITY_OPTIONS.find((o) => o.value === visibility) ?? null) : null;
  if (!current) return null;

  if (!canManage) {
    // Owner who can't manage (a guest — private-only pages). Show the state
    // read-only with a lock + tooltip so the capability is visible but clearly
    // gated to licensed accounts, rather than silently hidden.
    return (
      <span
        aria-label="Sharing options are available on licensed accounts"
        title="Sharing options are available on licensed accounts"
        style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0, fontSize: 12.5, fontWeight: 600, color: 'var(--fg-3)', padding: '6px 9px', cursor: 'default' }}
      >
        {visIcon(current.value)}
        <span style={{ whiteSpace: 'nowrap' }}>{current.trigger}</span>
      </span>
    );
  }

  return (
    <div style={{ position: 'relative', flexShrink: 0 }}>
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={menuOpen}
        aria-label="Who can view this page"
        disabled={saving}
        title={saveError ? "Couldn't update — try again" : 'Who can view this page'}
        onClick={() => {
          const r = triggerRef.current?.getBoundingClientRect();
          if (r) setDropUp(dropUpNearViewportBottom && window.innerHeight - r.bottom < 240);
          setMenuOpen((o) => !o);
        }}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          border: 'none',
          background: menuOpen ? 'var(--bg-2)' : 'transparent',
          color: saveError ? 'var(--he-red, #c2403a)' : 'var(--fg-2)',
          fontSize: 12.5,
          fontWeight: 600,
          fontFamily: 'inherit',
          padding: '6px 9px',
          borderRadius: 9,
          cursor: saving ? 'default' : 'pointer',
          opacity: saving ? 0.6 : 1,
        }}
      >
        {visIcon(current.value)}
        <span style={{ whiteSpace: 'nowrap' }}>{current.trigger}</span>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden style={{ opacity: 0.6 }}>
          <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {menuOpen && (
        <>
          <div onClick={() => setMenuOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 20 }} />
          <div
            role="menu"
            style={{
              position: 'absolute',
              right: 0,
              ...(dropUp ? { bottom: 'calc(100% + 4px)' } : { top: 'calc(100% + 4px)' }),
              zIndex: 21,
              background: 'var(--bg-1)',
              borderRadius: 12,
              padding: 5,
              minWidth: 232,
              boxShadow: '0 8px 28px rgba(0,0,0,.16)',
            }}
          >
            {VISIBILITY_OPTIONS.map((o) => {
              const selected = o.value === visibility;
              return (
                <button
                  key={o.value}
                  role="menuitemradio"
                  aria-checked={selected}
                  className="he-menuitem"
                  onClick={() => {
                    setMenuOpen(false);
                    changeVisibility(o.value);
                  }}
                  style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left', padding: '9px 10px', border: 'none', background: 'transparent', cursor: 'pointer', borderRadius: 8, color: 'var(--fg-1)', fontFamily: 'inherit' }}
                >
                  <span style={{ color: 'var(--fg-2)', display: 'flex' }}>{visIcon(o.value)}</span>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ display: 'block', fontSize: 13.5, fontWeight: 600 }}>{o.trigger}</span>
                    <span style={{ display: 'block', fontSize: 12, color: 'var(--fg-3)' }}>{o.label}</span>
                  </span>
                  {selected && (
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" aria-hidden style={{ color: 'var(--he-purple)', flexShrink: 0 }}>
                      <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
